import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  Snackbar,
  IconButton,
  Grid,
  Box,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ThemedDialogContent from "./ThemedDialogContent";
import ThemedDialogTitle from "./ThemedDialogTitle";
import ThemedDialogActions from "./ThemedDialogActions";
import ThemedTextField from "../ThemedTextField";
import ThemedSelect from "../ThemedSelect";
import ThemedTabs from "../ThemedTabs";
import MobileContactsTab from "../MobileContactsTab";
import MobileAddressesTab from "../MobileAddressesTab";
import { decoratedFetch } from "../../req_utils";
import {
  DARK_THEME_BODY_COLOR,
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
  GENESIS_LOGO_COLOR,
} from "../../constants";
import CloseIcon from "@mui/icons-material/Close";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ height: "100%" }}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, height: "100%" }}>{children}</Box>}
    </div>
  );
}

export default function MobileEditAccountDialog({
  parentState,
  createDialogOpen,
  handleClose,
  handleCreateClose,
  onAccountUpdate,
  onCreateAccount,
}) {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const [account, setAccount] = useState(null);
  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [notes, setNotes] = useState("");
  const [accountTypes, setAccountTypes] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const initializeAccountData = (selectedAccount) => {
    if (selectedAccount) {
      setAccount(selectedAccount);
      setAccountName(selectedAccount.name || "");
      setAccountType(selectedAccount.account_type?.id || "");
      setNotes(selectedAccount.notes || "");
    }
  };

  const resetCreateForm = () => {
    setAccountName("");
    setAccountType("");
    setCreateLoading(false);
  };

  const listAccountTypes = () => {
    decoratedFetch("/list_account_types")
      .then((response) => response.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setAccountTypes(data);
        } else {
          console.error("Expected an array of account types, received:", data);
          setAccountTypes([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching account types:", error);
        setAccountTypes([]);
      });
  };

  useEffect(() => {
    if (parentState !== null && parentState?.selectedAccount !== null) {
      initializeAccountData(parentState?.selectedAccount);
    } else {
      setAccount(null);
    }
    if (parentState !== null || createDialogOpen) listAccountTypes();
  }, [parentState, createDialogOpen]);

  useEffect(() => {
    if (createDialogOpen) {
      resetCreateForm();
    }
  }, [createDialogOpen]);

  const updateAccount = useCallback(
    (fieldName, value) => {
      if (!account) return;

      const payload = { [fieldName]: value };

      decoratedFetch(`/update_account/${account.id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (response.status === 200) {
            setSnackbarMessage("Account saved");
            if (onAccountUpdate) {
              const updatedAccount = { ...account };
              if (fieldName === "account_type_id") {
                const accountTypeObj = accountTypes.find(
                  (type) => type.id === value,
                );
                updatedAccount.account_type = accountTypeObj;
              } else if (fieldName === "notes") {
                updatedAccount.notes = value;
              }
              // Update local account state to reflect the change
              setAccount(updatedAccount);
              onAccountUpdate(updatedAccount);
            }
            return;
          }
          throw new Error("Error message generated");
        })
        .catch((error) => {
          console.error("Error saving account", error);
          setSnackbarMessage("Failed to save account");
        });
    },
    [account, accountTypes, onAccountUpdate],
  );

  const handleNotesChange = (event) => {
    const newNotes = event.target.value;
    setNotes(newNotes);
  };

  const handleNotesBlur = () => {
    if (notes !== account?.notes) {
      updateAccount("notes", notes);
    }
  };

  const handleAccountTypeChange = (event) => {
    const newAccountType = event.target.value;
    if (parentState !== null) {
      // Edit mode - save immediately
      if (newAccountType === account?.account_type?.id) {
        return;
      }
      setAccountType(newAccountType);
      updateAccount("account_type_id", newAccountType);
    } else {
      // Create mode - just update local state
      setAccountType(newAccountType);
    }
  };

  const handleCreateAccount = () => {
    if (!accountName || !accountType) {
      setSnackbarMessage("Please fill in all required fields");
      return;
    }

    setCreateLoading(true);
    const payload = {
      name: accountName,
      account_type_id: accountType,
    };

    decoratedFetch("/create_account", {
      method: "POST",
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (response.status === 201) {
          return response.json();
        }
        throw new Error("Failed to create account");
      })
      .then((data) => {
        setCreateLoading(false);
        setSnackbarMessage("Account created");
        if (onCreateAccount) {
          onCreateAccount(data);
        }
      })
      .catch((error) => {
        console.error("Error creating account:", error);
        setCreateLoading(false);
        setSnackbarMessage("Failed to create account");
      });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Snackbar
        sx={{
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        open={snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
      <Dialog
        fullScreen
        open={parentState !== null || createDialogOpen}
        onClose={parentState !== null ? handleClose : handleCreateClose}
        TransitionComponent={Transition}
        aria-labelledby="edit-account-dialog-title"
      >
        <ThemedDialogTitle
          id="edit-account-dialog-title"
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: "8px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: darkThemeEnabled
                  ? DARK_THEME_TEXT_COLOR
                  : LIGHT_THEME_TEXT_COLOR,
                fontSize: "1rem",
                fontWeight: "bold",
                marginRight: "18px",
              }}
            >
              {parentState !== null ? account?.name || "" : "Create Account"}
            </div>
            <div>
              <IconButton
                edge="start"
                color="inherit"
                onClick={parentState !== null ? handleClose : handleCreateClose}
                aria-label="close"
                style={{
                  color: darkThemeEnabled
                    ? DARK_THEME_TEXT_COLOR
                    : LIGHT_THEME_TEXT_COLOR,
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </ThemedDialogTitle>
        <ThemedDialogContent
          sx={{
            backgroundColor: darkThemeEnabled
              ? DARK_THEME_BODY_COLOR
              : "#f7f7f7",
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {parentState !== null ? (
            // Edit mode
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <ThemedTabs
                value={tabValue}
                onChange={handleTabChange}
                labels={["Details", "Contacts", "Addresses"]}
                variant="fullWidth"
                aria-label="account-details-tabs"
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: darkThemeEnabled
                    ? DARK_THEME_BODY_COLOR
                    : "#f7f7f7",
                }}
              />
              <Box sx={{ flex: 1, overflow: "auto" }}>
                <TabPanel value={tabValue} index={0}>
                  <Grid container sx={{ py: "20px", px: "15px" }}>
                    <Grid item xs={12}>
                      <ThemedSelect
                        labelId="account-type-label"
                        id="Account Type"
                        transparentLabel={true}
                        value={accountType}
                        renderValue={(accountTypeId) =>
                          accountTypeId
                            ? accountTypes.find(
                                (accountType) =>
                                  accountType?.id === accountTypeId,
                              )?.name
                            : ""
                        }
                        label="Account Type"
                        onChange={handleAccountTypeChange}
                        size="small"
                        fullWidth
                      >
                        {accountTypes.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </ThemedSelect>
                    </Grid>
                    <Grid item xs={12} sx={{ mt: "20px" }}>
                      <ThemedTextField
                        label="Notes"
                        fullWidth
                        variant="outlined"
                        value={notes}
                        onChange={handleNotesChange}
                        onBlur={handleNotesBlur}
                        multiline
                        rows={20}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <MobileContactsTab
                    open={tabValue === 1 && account?.id}
                    accountId={account?.id}
                    setSnackbarMessage={setSnackbarMessage}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <MobileAddressesTab
                    open={tabValue === 2 && account?.id}
                    accountId={account?.id}
                    setSnackbarMessage={setSnackbarMessage}
                  />
                </TabPanel>
              </Box>
            </Box>
          ) : (
            // Create mode
            <Grid container sx={{ py: "20px", px: "15px" }}>
              <Grid item xs={12}>
                <ThemedTextField
                  label="Account Name"
                  fullWidth
                  variant="outlined"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: "20px" }}>
                <ThemedSelect
                  labelId="account-type-label"
                  id="Account Type"
                  transparentLabel={true}
                  value={accountType}
                  renderValue={(accountTypeId) =>
                    accountTypeId
                      ? accountTypes.find(
                          (accountType) => accountType?.id === accountTypeId,
                        )?.name
                      : ""
                  }
                  label="Account Type"
                  onChange={handleAccountTypeChange}
                  size="small"
                  fullWidth
                >
                  {accountTypes.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </ThemedSelect>
              </Grid>
            </Grid>
          )}
        </ThemedDialogContent>
        {parentState === null && (
          <ThemedDialogActions>
            <LoadingButton
              loading={createLoading}
              disabled={!accountName || !accountType}
              sx={{
                backgroundColor: createLoading ? "#565656 !important" : "auto",
                "&.Mui-disabled": {
                  backgroundColor: darkThemeEnabled
                    ? "#666666 !important"
                    : "#e0e0e0 !important",
                  color: darkThemeEnabled
                    ? "#999999 !important"
                    : "#757575 !important",
                },
              }}
              variant="contained"
              onClick={handleCreateAccount}
            >
              Create
            </LoadingButton>
          </ThemedDialogActions>
        )}
      </Dialog>
    </>
  );
}
